import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon } from 'lucide-react';

import { cn } from '@components/utils/classname';

import {
  getCheckIconStyles,
  getIndicatorContainerStyles,
  getSelectItemStyles,
  getSelectLabelStyles,
  getSelectSeparatorStyles,
} from '../styles/select-styles';

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={getSelectLabelStyles(className)}
      {...props}
    />
  );
}

export interface SelectItemProps
  extends React.ComponentProps<typeof SelectPrimitive.Item> {
  asChild?: boolean;
  /** Hide the check icon indicator when item is selected */
  hideIndicator?: boolean;
  /** Secondary text displayed below or beside the main text */
  secondaryText?: React.ReactNode;
  /** Badge element displayed on the right side of the item */
  badge?: React.ReactNode;
}

function SelectItem({
  className,
  children,
  asChild,
  hideIndicator = false,
  secondaryText,
  badge,
  ...props
}: SelectItemProps) {
  const hasExtras = secondaryText || badge;

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        !asChild && getSelectItemStyles(),
        !asChild &&
          '*:[span]:last:flex *:[span]:last:flex-1 *:[span]:last:items-center *:[span]:last:gap-2',
        asChild &&
          'relative cursor-default outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {!hideIndicator && (
        <span className={getIndicatorContainerStyles()}>
          <SelectPrimitive.ItemIndicator>
            <CheckIcon className={getCheckIconStyles()} />
          </SelectPrimitive.ItemIndicator>
        </span>
      )}
      {asChild ? (
        children
      ) : hasExtras ? (
        <SelectPrimitive.ItemText asChild>
          <span className="flex flex-1 items-center justify-between gap-2">
            <span className="flex flex-col">
              <span>{children}</span>
              {secondaryText && (
                <span className="text-micro text-muted-foreground">
                  {secondaryText}
                </span>
              )}
            </span>
            {badge && <span className="shrink-0">{badge}</span>}
          </span>
        </SelectPrimitive.ItemText>
      ) : (
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      )}
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={getSelectSeparatorStyles(className)}
      {...props}
    />
  );
}

export { SelectItem, SelectLabel, SelectSeparator };
